import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  PencilSimple,
  Trash,
  CurrencyInr,
  Clock,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  ArrowsClockwise,
  Lightning,
  Timer
} from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { Modal, Button } from '../../components/common';
import { serviceService, addonService } from '../../services';
import './Pricing.css';

const pricingModels = ['PerKg', 'PerItem', 'PerPair'];
const garmentCategories = ['Men', 'Women', 'Kids', 'Shoes', 'Boots', 'Sandals', 'Slippers', 'Others', 'Household'];

export function Pricing({ onMenuClick }) {
  const [activeTab, setActiveTab] = useState('categories');
  const [servicesList, setServicesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPricingModel, setFilterPricingModel] = useState('all');
  const [expandedCards, setExpandedCards] = useState({});

  const [addonsList, setAddonsList] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [addonsError, setAddonsError] = useState(null);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [showDeleteAddonModal, setShowDeleteAddonModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [isEditingAddon, setIsEditingAddon] = useState(false);
  const [addonForm, setAddonForm] = useState({ name: '', price: '' });
  const [addonFormErrors, setAddonFormErrors] = useState({});

  const [formData, setFormData] = useState({
    categoryName: '',
    serviceCode: '',
    pricingModel: 'PerKg',
    pricePerKg: '',
    pricePerPair: '',
    standardTAT: '',
    semiExpressTAT: '',
    expressTAT: '',
    semiExpressPriceMultiplier: 1.5,
    expressPriceMultiplier: 2,
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    itemName: '',
    garmentCode: '',
    garmentCategory: 'Men',
    price: '',
  });

  const [formErrors, setFormErrors] = useState({});

  // Fetch services from API
  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await serviceService.getServices();
      setServicesList(data);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const fetchAddons = useCallback(async () => {
    try {
      setAddonsLoading(true);
      setAddonsError(null);
      const data = await addonService.getAddons();
      setAddonsList(data);
    } catch (err) {
      console.error('Error fetching addons:', err);
      setAddonsError('Failed to load addons. Please try again.');
    } finally {
      setAddonsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  const validateForm = () => {
    const errors = {};
    if (!formData.categoryName.trim()) errors.categoryName = 'Category name is required';
    if (!formData.serviceCode.trim()) errors.serviceCode = 'Service code is required';
    if (!formData.pricingModel) errors.pricingModel = 'Pricing model is required';

    if (formData.pricingModel === 'PerKg' && (!formData.pricePerKg || formData.pricePerKg <= 0)) {
      errors.pricePerKg = 'Valid price per kg is required';
    }
    if (formData.pricingModel === 'PerPair' && (!formData.pricePerPair || formData.pricePerPair <= 0)) {
      errors.pricePerPair = 'Valid price per pair is required';
    }
    if (!formData.standardTAT.trim()) errors.standardTAT = 'Standard TAT is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSubcategoryForm = () => {
    const errors = {};
    if (!subcategoryForm.itemName.trim()) errors.itemName = 'Item name is required';
    if (!subcategoryForm.garmentCode.trim()) errors.garmentCode = 'Garment code is required';
    if (!subcategoryForm.price || subcategoryForm.price <= 0) errors.price = 'Valid price is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddService = () => {
    setIsEditing(false);
    setSelectedService(null);
    setFormData({
      categoryName: '',
      serviceCode: '',
      pricingModel: 'PerKg',
      pricePerKg: '',
      pricePerPair: '',
      standardTAT: '',
      semiExpressTAT: '',
      expressTAT: '',
      semiExpressPriceMultiplier: 1.5,
      expressPriceMultiplier: 2,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditService = (service) => {
    setIsEditing(true);
    setSelectedService(service);
    setFormData({
      categoryName: service.categoryName,
      serviceCode: service.serviceCode,
      pricingModel: service.pricingModel,
      pricePerKg: service.pricePerKg || '',
      pricePerPair: service.pricePerPair || '',
      standardTAT: service.standardTAT || '',
      semiExpressTAT: service.semiExpressTAT || '',
      expressTAT: service.expressTAT || '',
      semiExpressPriceMultiplier: service.semiExpressPriceMultiplier || 1.5,
      expressPriceMultiplier: service.expressPriceMultiplier || 2,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteClick = (service) => {
    setSelectedService(service);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await serviceService.deleteService(selectedService._id);
      setServicesList((prev) => prev.filter((s) => s._id !== selectedService._id));
      setShowDeleteModal(false);
      setSelectedService(null);
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('Failed to delete service. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const serviceData = {
        categoryName: formData.categoryName,
        serviceCode: formData.serviceCode,
        pricingModel: formData.pricingModel,
        standardTAT: formData.standardTAT,
        semiExpressTAT: formData.semiExpressTAT,
        expressTAT: formData.expressTAT,
        semiExpressPriceMultiplier: parseFloat(formData.semiExpressPriceMultiplier) || 1.5,
        expressPriceMultiplier: parseFloat(formData.expressPriceMultiplier) || 2,
      };

      if (formData.pricingModel === 'PerKg') {
        serviceData.pricePerKg = parseFloat(formData.pricePerKg);
      } else if (formData.pricingModel === 'PerPair') {
        serviceData.pricePerPair = parseFloat(formData.pricePerPair);
      }

      if (isEditing) {
        const updatedService = await serviceService.updateService(selectedService._id, serviceData);
        setServicesList((prev) =>
          prev.map((s) => (s._id === selectedService._id ? updatedService : s))
        );
      } else {
        const newService = await serviceService.createService(serviceData);
        setServicesList((prev) => [...prev, newService]);
      }

      setShowModal(false);
      setFormData({
        categoryName: '',
        serviceCode: '',
        pricingModel: 'PerKg',
        pricePerKg: '',
        pricePerPair: '',
        standardTAT: '',
        semiExpressTAT: '',
        expressTAT: '',
        semiExpressPriceMultiplier: 1.5,
        expressPriceMultiplier: 2,
      });
      setFormErrors({});
    } catch (err) {
      console.error('Error saving service:', err);
      alert('Failed to save service. Please try again.');
    }
  };

  const handleAddSubcategory = (service) => {
    setSelectedService(service);
    setSubcategoryForm({
      itemName: '',
      garmentCode: '',
      garmentCategory: 'Men',
      price: '',
      editIndex: null,
    });
    setFormErrors({});
    setShowSubcategoryModal(true);
  };

  const handleSubcategorySubmit = async (e) => {
    e?.preventDefault?.();

    if (!validateSubcategoryForm()) return;

    // Packaging is managed under Pricing → Addons (flat fee), not as a
    // PerItem subcategory — having both produced duplicate invoice lines.
    if (/^packaging$/i.test(subcategoryForm.itemName.trim())) {
      alert('Packaging is managed under the Addons tab (flat fee). Switch to Addons to edit its price.');
      setShowSubcategoryModal(false);
      setActiveTab('addons');
      return;
    }

    try {
      const subcategoryData = {
        itemName: subcategoryForm.itemName,
        garmentCode: subcategoryForm.garmentCode,
        garmentCategory: subcategoryForm.garmentCategory,
        price: parseFloat(subcategoryForm.price),
      };

      let updatedSubcategories;
      if (subcategoryForm.editIndex !== null && subcategoryForm.editIndex !== undefined) {
        // Edit existing subcategory
        updatedSubcategories = [...(selectedService.subcategories || [])];
        updatedSubcategories[subcategoryForm.editIndex] = subcategoryData;
      } else {
        // Add new subcategory
        updatedSubcategories = [...(selectedService.subcategories || []), subcategoryData];
      }

      const updatedService = await serviceService.updateService(selectedService._id, {
        subcategories: updatedSubcategories,
      });

      setServicesList((prev) =>
        prev.map((s) => (s._id === selectedService._id ? updatedService : s))
      );

      setShowSubcategoryModal(false);
      setSubcategoryForm({
        itemName: '',
        garmentCode: '',
        garmentCategory: 'Men',
        price: '',
        editIndex: null,
      });
    } catch (err) {
      console.error('Error saving subcategory:', err);
      alert(err.response?.data?.message || 'Failed to save item. Please try again.');
    }
  };

  const handleEditSubcategory = (service, subcategoryIndex) => {
    const subcategory = service.subcategories[subcategoryIndex];
    setSelectedService(service);
    setSubcategoryForm({
      itemName: subcategory.itemName,
      garmentCode: subcategory.garmentCode,
      garmentCategory: subcategory.garmentCategory,
      price: subcategory.price,
      editIndex: subcategoryIndex,
    });
    setFormErrors({});
    setShowSubcategoryModal(true);
  };

  const handleDeleteSubcategory = async (service, subcategoryIndex) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const updatedSubcategories = service.subcategories.filter((_, idx) => idx !== subcategoryIndex);
      const updatedService = await serviceService.updateService(service._id, {
        subcategories: updatedSubcategories,
      });

      setServicesList((prev) =>
        prev.map((s) => (s._id === service._id ? updatedService : s))
      );
    } catch (err) {
      console.error('Error deleting subcategory:', err);
      alert('Failed to delete item. Please try again.');
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'addons') {
      fetchAddons();
    } else {
      fetchServices();
    }
  };

  const validateAddonForm = () => {
    const errors = {};
    if (!addonForm.name.trim()) errors.name = 'Name is required';
    if (!addonForm.price || addonForm.price <= 0) errors.price = 'Valid price is required';
    setAddonFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddAddon = () => {
    setIsEditingAddon(false);
    setSelectedAddon(null);
    setAddonForm({ name: '', price: '' });
    setAddonFormErrors({});
    setShowAddonModal(true);
  };

  const handleEditAddon = (addon) => {
    setIsEditingAddon(true);
    setSelectedAddon(addon);
    setAddonForm({ name: addon.name, price: addon.price });
    setAddonFormErrors({});
    setShowAddonModal(true);
  };

  const handleAddonSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateAddonForm()) return;

    try {
      const addonData = { name: addonForm.name.trim(), price: parseFloat(addonForm.price) };

      if (isEditingAddon) {
        const updatedAddon = await addonService.updateAddon(selectedAddon._id, addonData);
        setAddonsList((prev) => prev.map((a) => (a._id === selectedAddon._id ? updatedAddon : a)));
      } else {
        const newAddon = await addonService.createAddon(addonData);
        setAddonsList((prev) => [...prev, newAddon]);
      }

      setShowAddonModal(false);
      setAddonForm({ name: '', price: '' });
      setAddonFormErrors({});
    } catch (err) {
      console.error('Error saving addon:', err);
      alert(err.response?.data?.message || 'Failed to save addon. Please try again.');
    }
  };

  const handleDeleteAddonClick = (addon) => {
    setSelectedAddon(addon);
    setShowDeleteAddonModal(true);
  };

  const handleDeleteAddonConfirm = async () => {
    try {
      await addonService.deleteAddon(selectedAddon._id);
      setAddonsList((prev) => prev.filter((a) => a._id !== selectedAddon._id));
      setShowDeleteAddonModal(false);
      setSelectedAddon(null);
    } catch (err) {
      console.error('Error deleting addon:', err);
      alert('Failed to delete addon. Please try again.');
    }
  };

  const toggleCardExpand = (serviceId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  };

  const getPricingModelLabel = (model) => {
    const labels = {
      'PerKg': 'Per Kg',
      'PerItem': 'Per Item',
      'PerPair': 'Per Pair',
    };
    return labels[model] || model;
  };

  const getPricingModelBadgeClass = (model) => {
    const classes = {
      'PerKg': 'badge-primary',
      'PerItem': 'badge-info',
      'PerPair': 'badge-warning',
    };
    return classes[model] || 'badge-neutral';
  };

  const getMainPrice = (service) => {
    if (service.pricingModel === 'PerKg') return service.pricePerKg;
    if (service.pricingModel === 'PerPair') return service.pricePerPair;
    return null;
  };

  // Filter services list
  const filteredServices = servicesList.filter((service) => {
    const matchesSearch =
      service.categoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.serviceCode.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPricingModel = filterPricingModel === 'all' || service.pricingModel === filterPricingModel;
    return matchesSearch && matchesPricingModel;
  });

  return (
    <>
      <Header
        title="Pricing"
        subtitle="Manage service pricing and packages"
        onMenuClick={onMenuClick}
        actions={
          <div className="header-actions-group">
            <button className="header-icon-btn" onClick={handleRefresh} title="Refresh">
              <ArrowsClockwise size={18} />
            </button>
            {activeTab === 'addons' ? (
              <Button icon={<Plus size={18} />} onClick={handleAddAddon}>
                Add Addon
              </Button>
            ) : (
              <Button icon={<Plus size={18} />} onClick={handleAddService}>
                Add Service
              </Button>
            )}
          </div>
        }
      />
      <div className="page-content">
        <div className="pricing-tabs">
          <button
            className={`pricing-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`pricing-tab ${activeTab === 'addons' ? 'active' : ''}`}
            onClick={() => setActiveTab('addons')}
          >
            Addons
          </button>
        </div>

        {activeTab === 'categories' && (
        <>
        {/* Search and Filter */}
        <div className="search-filter-bar">
          <div className="search-wrapper">
            <MagnifyingGlass className="search-icon" size={20} />
            <input
              type="text"
              className="search-input"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={filterPricingModel}
            onChange={(e) => setFilterPricingModel(e.target.value)}
          >
            <option value="all">All Pricing Models</option>
            {pricingModels.map((model) => (
              <option key={model} value={model}>{getPricingModelLabel(model)}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <Button variant="secondary" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        )}

        {loading ? (
          <div className="loading">
            <div className="spinner" />
            <span style={{ marginTop: 12 }}>Loading services...</span>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💰</div>
            <p>No services found</p>
            <Button onClick={handleAddService}>Add First Service</Button>
          </div>
        ) : (
          <div className="table-container">
            <table className="table pricing-table">
              <thead>
                <tr>
                  <th>Service Name</th>
                  <th>Code</th>
                  <th>Pricing Model</th>
                  <th className="text-right">Price</th>
                  <th>Standard TAT</th>
                  <th>Semi Express</th>
                  <th>Express</th>
                  <th>Items</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((service) => (
                  <React.Fragment key={service._id}>
                    <tr className="service-row">
                      <td>
                        <div className="service-name-cell">
                          <CurrencyInr size={16} className="service-icon" />
                          <span className="service-name">{service.categoryName}</span>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-neutral">{service.serviceCode}</span>
                      </td>
                      <td>
                        <span className={`badge ${getPricingModelBadgeClass(service.pricingModel)}`}>
                          {getPricingModelLabel(service.pricingModel)}
                        </span>
                      </td>
                      <td className="text-right">
                        {getMainPrice(service) ? (
                          <span className="price-value">₹{getMainPrice(service)}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <div className="tat-cell">
                          <Clock size={14} />
                          <span>{service.standardTAT}</span>
                        </div>
                      </td>
                      <td>
                        {service.semiExpressTAT ? (
                          <div className="tat-cell">
                            <Timer size={14} />
                            <span>{service.semiExpressTAT}</span>
                            <span className="multiplier">({service.semiExpressPriceMultiplier || 1.5}x)</span>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {service.expressTAT ? (
                          <div className="tat-cell">
                            <Lightning size={14} />
                            <span>{service.expressTAT}</span>
                            <span className="multiplier">({service.expressPriceMultiplier || 2}x)</span>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {service.pricingModel === 'PerItem' ? (
                          <button
                            className="items-toggle"
                            onClick={() => toggleCardExpand(service._id)}
                          >
                            {service.subcategories?.length || 0} items
                            {expandedCards[service._id] ? <CaretUp size={14} /> : <CaretDown size={14} />}
                          </button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="action-btn"
                            title="Edit"
                            onClick={() => handleEditService(service)}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            className="action-btn danger"
                            title="Delete"
                            onClick={() => handleDeleteClick(service)}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {service.pricingModel === 'PerItem' && expandedCards[service._id] && (
                      <tr className="subcategories-row">
                        <td colSpan="9">
                          <div className="subcategories-container">
                            <div className="subcategories-grid">
                              {service.subcategories?.map((sub, idx) => (
                                <div key={idx} className="subcategory-item">
                                  <div className="subcategory-info">
                                    <span className="subcategory-name">{sub.itemName}</span>
                                    <span className="subcategory-meta">
                                      {sub.garmentCode} | {sub.garmentCategory}
                                    </span>
                                  </div>
                                  <div className="subcategory-actions">
                                    <span className="subcategory-price">₹{sub.price}</span>
                                    <button
                                      className="action-btn-small"
                                      title="Edit"
                                      onClick={() => handleEditSubcategory(service, idx)}
                                    >
                                      <PencilSimple size={12} />
                                    </button>
                                    <button
                                      className="action-btn-small danger"
                                      title="Delete"
                                      onClick={() => handleDeleteSubcategory(service, idx)}
                                    >
                                      <Trash size={12} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <button
                              className="add-subcategory-btn"
                              onClick={() => handleAddSubcategory(service)}
                            >
                              <Plus size={14} /> Add Item
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {activeTab === 'addons' && (
          <>
            {addonsError && (
              <div className="error-banner">
                <span>{addonsError}</span>
                <Button variant="secondary" size="small" onClick={handleRefresh}>
                  Retry
                </Button>
              </div>
            )}

            <p className="addons-hint">
              Flat-fee extras (e.g. Packaging). Edit the price here — it applies to new orders when the addon is selected.
            </p>

            {addonsLoading ? (
              <div className="loading">
                <div className="spinner" />
                <span style={{ marginTop: 12 }}>Loading addons...</span>
              </div>
            ) : addonsList.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📦</div>
                <p>No addons found</p>
                <Button onClick={handleAddAddon}>Add First Addon</Button>
              </div>
            ) : (
              <div className="table-container">
                <table className="table pricing-table">
                  <thead>
                    <tr>
                      <th>Addon Name</th>
                      <th className="text-right">Price (₹)</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addonsList.map((addon) => (
                      <tr key={addon._id} className="service-row">
                        <td>
                          <div className="service-name-cell">
                            <CurrencyInr size={16} className="service-icon" />
                            <span className="service-name">{addon.name}</span>
                          </div>
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="price-value price-value--editable"
                            title="Edit price"
                            onClick={() => handleEditAddon(addon)}
                          >
                            ₹{addon.price}
                          </button>
                        </td>
                        <td>
                          <div className="actions-cell">
                            <button
                              className="action-btn"
                              title="Edit"
                              onClick={() => handleEditAddon(addon)}
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              className="action-btn danger"
                              title="Delete"
                              onClick={() => handleDeleteAddonClick(addon)}
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Service Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? 'Edit Service' : 'Add New Service'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {isEditing ? 'Save Changes' : 'Add Service'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category Name *</label>
              <input
                type="text"
                className={`form-input ${formErrors.categoryName ? 'error' : ''}`}
                placeholder="e.g., Wash & Fold"
                value={formData.categoryName}
                onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
              />
              {formErrors.categoryName && <span className="form-error">{formErrors.categoryName}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Service Code *</label>
              <input
                type="text"
                className={`form-input ${formErrors.serviceCode ? 'error' : ''}`}
                placeholder="e.g., WF"
                value={formData.serviceCode}
                onChange={(e) => setFormData({ ...formData, serviceCode: e.target.value.toUpperCase() })}
              />
              {formErrors.serviceCode && <span className="form-error">{formErrors.serviceCode}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Pricing Model *</label>
            <select
              className={`form-select ${formErrors.pricingModel ? 'error' : ''}`}
              value={formData.pricingModel}
              onChange={(e) => setFormData({ ...formData, pricingModel: e.target.value })}
            >
              {pricingModels.map((model) => (
                <option key={model} value={model}>
                  {getPricingModelLabel(model)}
                </option>
              ))}
            </select>
            {formErrors.pricingModel && <span className="form-error">{formErrors.pricingModel}</span>}
          </div>

          {formData.pricingModel === 'PerKg' && (
            <div className="form-group">
              <label className="form-label">Price Per Kg (₹) *</label>
              <input
                type="number"
                className={`form-input ${formErrors.pricePerKg ? 'error' : ''}`}
                placeholder="0"
                min="0"
                step="0.01"
                value={formData.pricePerKg}
                onChange={(e) => setFormData({ ...formData, pricePerKg: e.target.value })}
              />
              {formErrors.pricePerKg && <span className="form-error">{formErrors.pricePerKg}</span>}
            </div>
          )}

          {formData.pricingModel === 'PerPair' && (
            <div className="form-group">
              <label className="form-label">Price Per Pair (₹) *</label>
              <input
                type="number"
                className={`form-input ${formErrors.pricePerPair ? 'error' : ''}`}
                placeholder="0"
                min="0"
                step="0.01"
                value={formData.pricePerPair}
                onChange={(e) => setFormData({ ...formData, pricePerPair: e.target.value })}
              />
              {formErrors.pricePerPair && <span className="form-error">{formErrors.pricePerPair}</span>}
            </div>
          )}

          <div className="form-section-title">Turnaround Time (TAT)</div>

          <div className="form-group">
            <label className="form-label">Standard TAT *</label>
            <input
              type="text"
              className={`form-input ${formErrors.standardTAT ? 'error' : ''}`}
              placeholder="e.g., 48 hours"
              value={formData.standardTAT}
              onChange={(e) => setFormData({ ...formData, standardTAT: e.target.value })}
            />
            {formErrors.standardTAT && <span className="form-error">{formErrors.standardTAT}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Semi Express TAT</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., 24 hours"
                value={formData.semiExpressTAT}
                onChange={(e) => setFormData({ ...formData, semiExpressTAT: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Price Multiplier</label>
              <input
                type="number"
                className="form-input"
                placeholder="1.5"
                min="1"
                step="0.1"
                value={formData.semiExpressPriceMultiplier}
                onChange={(e) => setFormData({ ...formData, semiExpressPriceMultiplier: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Express TAT</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., 12 hours"
                value={formData.expressTAT}
                onChange={(e) => setFormData({ ...formData, expressTAT: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Price Multiplier</label>
              <input
                type="number"
                className="form-input"
                placeholder="2"
                min="1"
                step="0.1"
                value={formData.expressPriceMultiplier}
                onChange={(e) => setFormData({ ...formData, expressPriceMultiplier: e.target.value })}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Add Subcategory Modal */}
      <Modal
        isOpen={showSubcategoryModal}
        onClose={() => setShowSubcategoryModal(false)}
        title={subcategoryForm.editIndex !== null ? `Edit Item in ${selectedService?.categoryName}` : `Add Item to ${selectedService?.categoryName}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowSubcategoryModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubcategorySubmit}>
              {subcategoryForm.editIndex !== null ? 'Save Changes' : 'Add Item'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubcategorySubmit}>
          <div className="form-group">
            <label className="form-label">Item Name *</label>
            <input
              type="text"
              className={`form-input ${formErrors.itemName ? 'error' : ''}`}
              placeholder="e.g., Shirt"
              value={subcategoryForm.itemName}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, itemName: e.target.value })}
            />
            {formErrors.itemName && <span className="form-error">{formErrors.itemName}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Garment Code *</label>
              <input
                type="text"
                className={`form-input ${formErrors.garmentCode ? 'error' : ''}`}
                placeholder="e.g., SHT"
                value={subcategoryForm.garmentCode}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, garmentCode: e.target.value.toUpperCase() })}
              />
              {formErrors.garmentCode && <span className="form-error">{formErrors.garmentCode}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className="form-select"
                value={subcategoryForm.garmentCategory}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, garmentCategory: e.target.value })}
              >
                {garmentCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Price (₹) *</label>
            <input
              type="number"
              className={`form-input ${formErrors.price ? 'error' : ''}`}
              placeholder="0"
              min="0"
              step="0.01"
              value={subcategoryForm.price}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, price: e.target.value })}
            />
            {formErrors.price && <span className="form-error">{formErrors.price}</span>}
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Service"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{selectedService?.categoryName}</strong>?
          This action cannot be undone and will remove all associated items.
        </p>
      </Modal>

      {/* Add/Edit Addon Modal */}
      <Modal
        isOpen={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        title={isEditingAddon ? 'Edit Addon' : 'Add New Addon'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddonModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddonSubmit}>
              {isEditingAddon ? 'Save Changes' : 'Add Addon'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleAddonSubmit}>
          <div className="form-group">
            <label className="form-label">Addon Name *</label>
            <input
              type="text"
              className={`form-input ${addonFormErrors.name ? 'error' : ''}`}
              placeholder="e.g., Packaging"
              value={addonForm.name}
              onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
            />
            {addonFormErrors.name && <span className="form-error">{addonFormErrors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Price (₹) *</label>
            <input
              type="number"
              className={`form-input ${addonFormErrors.price ? 'error' : ''}`}
              placeholder="0"
              min="0"
              step="0.01"
              autoFocus={isEditingAddon}
              value={addonForm.price}
              onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value })}
            />
            {addonFormErrors.price && <span className="form-error">{addonFormErrors.price}</span>}
            <span className="form-hint">This unit price is used when staff add the addon on an order.</span>
          </div>
        </form>
      </Modal>

      {/* Delete Addon Confirmation Modal */}
      <Modal
        isOpen={showDeleteAddonModal}
        onClose={() => setShowDeleteAddonModal(false)}
        title="Delete Addon"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteAddonModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAddonConfirm}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{selectedAddon?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>
    </>
  );
}

export default Pricing;
